/**
 * AuthContext — Admin Panel
 * Il Diario di Bordo - SaaS
 *
 * MIGRAZIONE httpOnly Cookie:
 * - I token JWT sono gestiti via cookie httpOnly (impostati dal backend)
 * - Il frontend NON legge/scrive più token in localStorage
 * - checkAuth() chiama /auth/me (il cookie viene inviato automaticamente)
 * - logout() chiama /auth/logout (il backend cancella i cookie)
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import api, { setOnForceLogout } from '../services/api'

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Stato per 2FA pending
  const [twoFactorPending, setTwoFactorPending] = useState(null)

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      // Il cookie httpOnly viene inviato automaticamente
      const response = await api.get('/auth/me')

      // Verify user is superadmin
      if (response.data.data.user.role !== 'superadmin') {
        throw new Error('Accesso non autorizzato')
      }

      setUser(response.data.data.user)
    } catch (err) {
      console.error('Auth check failed:', err)
      // Nessun cookie valido o sessione scaduta — utente non autenticato
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password) => {
    setError(null)

    try {
      const response = await api.post('/auth/login', { email, password })

      // Backend risponde con requires2FA a livello ROOT
      if (response.data.requires2FA) {
        const data = response.data.data

        setTwoFactorPending({
          twoFactorToken: data.twoFactorToken,
          methods: data.methods,
          canRememberDevice: data.canRememberDevice || false
        })

        return {
          success: true,
          twoFactorRequired: true,
          methods: data.methods
        }
      }

      // Login normale senza 2FA — cookie httpOnly già impostati dal backend
      const { user } = response.data.data

      // Verify user is superadmin
      if (user.role !== 'superadmin') {
        throw new Error('Accesso riservato agli amministratori')
      }

      setUser(user)
      return { success: true, twoFactorRequired: false }
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Errore durante il login'
      setError(message)
      return { success: false, error: message }
    }
  }

  // Completa login dopo verifica 2FA
  // I cookie httpOnly sono già stati impostati dal backend nella risposta 2FA verify
  const complete2FALogin = async (accessToken, refreshToken, userData) => {
    try {
      // Verify user is superadmin
      if (userData.role !== 'superadmin') {
        throw new Error('Accesso riservato agli amministratori')
      }

      // Cookie già impostati dal backend — aggiorna solo lo state React
      setUser(userData)
      setTwoFactorPending(null)

      return { success: true }
    } catch (err) {
      const message = err.message || 'Errore nel completamento login'
      setError(message)
      return { success: false, error: message }
    }
  }

  // Annulla processo 2FA
  const cancel2FA = () => {
    setTwoFactorPending(null)
    setError(null)
  }

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout') // Il backend cancella i cookie httpOnly
    } catch (err) {
      console.error('Logout API error:', err)
    }
    setUser(null)
    setTwoFactorPending(null)
    setError(null)
  }, [])

  // Registra callback per force-logout dall'interceptor API
  useEffect(() => {
    setOnForceLogout(logout)
    return () => setOnForceLogout(null)
  }, [logout])

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'superadmin',
    // 2FA
    twoFactorPending,
    setTwoFactorPending,
    complete2FALogin,
    cancel2FA,
    // Actions
    login,
    logout,
    checkAuth
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
